import { Info } from 'lucide-react'

import type { IssueSeverity } from '@/shared/constants/issue-status'

import { ageDays } from '../../../lib/issues'

import type { GisIssue } from '../../../api/types'

/** Severity → solid badge colour for the site-issue avatar (mock `.site-issue` square). */
const SEV_TOKEN: Record<IssueSeverity, string> = {
  critical: 'bg-gis-red',
  non_critical: 'bg-gis-orange',
}

/** Sort by severity (critical first) then by age desc, so the worst/oldest float to the top. */
const SEV_RANK: Record<IssueSeverity, number> = { critical: 0, non_critical: 1 }

export function sortSiteIssues(list: GisIssue[]): GisIssue[] {
  return [...list].sort((a, b) => {
    const sev = SEV_RANK[a.severity] - SEV_RANK[b.severity]
    return sev !== 0 ? sev : ageDays(b.createdAt) - ageDays(a.createdAt)
  })
}

/** One row in "Issues at this site" — view-only (no expand/drill for now): severity badge, title,
 *  category + age. Rendered as a static row, not an interactive control. */
export function SiteIssueRow({ issue: i }: { issue: GisIssue }) {
  return (
    <div className="flex w-full items-center gap-[13px] rounded-[12px] px-3 py-[10px]">
      <div
        className={`grid h-[24px] w-[24px] flex-none place-items-center rounded-[8px] text-[11.5px] font-bold text-white ${SEV_TOKEN[i.severity]}`}
      >
        {i.severity.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-semibold text-gis-ink">{i.title}</div>
        <div className="mt-[3px] truncate text-[11px] font-medium text-gis-soft">
          {[i.category, `${ageDays(i.createdAt)}d old`].filter(Boolean).join(' · ')}
        </div>
      </div>
    </div>
  )
}

/** "12 Mar 2026, 10:24 AM" — same date/time locales as the issue drawer's facts. */
function formatUpdated(iso: string): string {
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date}, ${time}`
}

/** Drawer footer (mock bottom row): data-provenance note + the latest survey/field-update time.
 *  `lastUpdated` null hides the right side (sites with no surveys or activity yet). */
export function SiteFooterNote({ lastUpdated }: { lastUpdated: string | null }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gis-line-2 pt-3 text-[11px] font-medium text-gis-soft">
      <span className="flex items-center gap-[6px]">
        <Info className="h-[13px] w-[13px] flex-none stroke-2 text-gis-muted" />
        Data shown is based on the latest survey and field updates.
      </span>
      {lastUpdated && (
        <span className="ml-auto text-gis-muted tabular-nums">Last updated: {formatUpdated(lastUpdated)}</span>
      )}
    </div>
  )
}
